import { useState } from 'react';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import type { SetScoreInput } from './badmintonScoringUtils';
import BadmintonPointPickerSheet from './BadmintonPointPickerSheet';

interface BadmintonScorePadProps {
  maxSets: number;
  activeSet: number;
  sets: SetScoreInput[];
  current: SetScoreInput;
  scoringType: string;
  onSelectSet: (setNumber: number) => void;
  onBump: (side: 'a' | 'b', delta: number) => void;
  onSetScore: (side: 'a' | 'b', value: number) => void;
}

export default function BadmintonScorePad({
  maxSets,
  activeSet,
  sets,
  current,
  scoringType,
  onSelectSet,
  onBump,
  onSetScore,
}: BadmintonScorePadProps) {
  const [pickerSide, setPickerSide] = useState<'a' | 'b' | null>(null);
  const pickerValue = pickerSide === 'b' ? current.scoreSideB : current.scoreSideA;

  return (
    <>
      <div className="mt-3 flex gap-2">
        {sets.map((set) => {
          const selected = activeSet === set.setNumber;
          return (
            <Button
              key={set.setNumber}
              type="button"
              variant="outline"
              onClick={() => onSelectSet(set.setNumber)}
              className={`h-auto min-h-0 flex-1 flex-col gap-0 rounded-full px-3 py-1 leading-none shadow-none ${
                selected
                  ? 'border-[#FC480D] bg-[#FFF8F5] text-[#FC480D]'
                  : 'border-transparent bg-gray-100 text-gray-400 hover:bg-gray-100'
              }`}
            >
              <span className="text-xs font-bold">Set {set.setNumber}</span>
              <span className="text-[10px] font-medium">
                {set.scoreSideA}-{set.scoreSideB}
              </span>
            </Button>
          );
        })}
      </div>

      <Card className="mt-3 rounded-2xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]">
        <p className="mb-3 text-sm font-semibold text-gray-500">
          Set {activeSet} dari {maxSets}
        </p>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          {(['a', 'b'] as const).map((side, index) => {
            const value = side === 'a' ? current.scoreSideA : current.scoreSideB;
            const picking = pickerSide === side;
            return (
              <div key={side} className="contents">
                {index === 1 && <span className="text-lg font-semibold text-gray-300">-</span>}
                <Button
                  type="button"
                  variant="secondary"
                  aria-label={side === 'a' ? 'Poin sisi kiri' : 'Poin sisi kanan'}
                  onClick={() => setPickerSide(side)}
                  className={`h-28 w-full rounded-2xl bg-gray-50 text-5xl font-black text-[#0E1726] ${
                    picking ? 'border border-[#FC480D] bg-[#FFF8F5] text-[#FC480D]' : ''
                  }`}
                >
                  {value}
                </Button>
              </div>
            );
          })}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-8">
          {(['a', 'b'] as const).map((side) => (
            <div key={`bump-${side}`} className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="secondary"
                aria-label={side === 'a' ? 'Kurangi poin sisi kiri' : 'Kurangi poin sisi kanan'}
                onClick={() => onBump(side, -1)}
                className="h-11 rounded-2xl bg-gray-100 text-lg font-bold text-gray-500"
              >
                −
              </Button>
              <Button
                type="button"
                variant="secondary"
                aria-label={side === 'a' ? 'Tambah poin sisi kiri' : 'Tambah poin sisi kanan'}
                onClick={() => onBump(side, 1)}
                className="h-11 rounded-2xl bg-[#FFF1EC] text-lg font-bold text-[#FC480D] hover:bg-[#FFE4D8]"
              >
                +
              </Button>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-xs font-semibold text-[#FC480D]">
          Belum ada servis dicatat
        </p>
        <p className="mt-2 text-center text-xs leading-relaxed text-gray-400">
          Tap angka untuk memilih poin akhir, atau pakai tombol tambah/kurang untuk mencatat
          manual.
        </p>
      </Card>

      <BadmintonPointPickerSheet
        open={pickerSide != null}
        side={pickerSide}
        currentValue={pickerValue}
        scoringType={scoringType}
        onClose={() => setPickerSide(null)}
        onSelect={(value) => {
          if (pickerSide) onSetScore(pickerSide, value);
          setPickerSide(null);
        }}
      />
    </>
  );
}
